import React, { useEffect, useState } from "react";
import { addUpTime, convertMinutesToHours, timePercentage } from "../utils/timeUtils";
import { Card, CardHeader, CardContent, Grid, Typography, LinearProgress, IconButton, TextField, Box, Select, MenuItem } from "@mui/material"
import styled from "@emotion/styled"
import { Edit, Check, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import { updateProject } from "../redux/slices/project";

const InfoCard = styled(Card)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 1',
    }
}));

const CardTitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
}));

const ContentGridSpaced = styled(Grid)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
    alignItems: 'center',
}));

const ProgressBarCaption = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    paddingBottom: theme.spacing(2),
}));

const CardHeaderContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const ProjectBudgetTime = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const project = useSelector(state => state.project.currentProject);
    const isLocked = useIsProjectLocked(project);
    const [isEditing, setIsEditing] = useState(false);
    const [localBudget, setLocalBudget] = useState({
        quoted: project?.budget?.quoted || 0,
        budgeted: project?.budget?.budgeted || 0,
        billingType: project?.billingTerms?.type || 'Hourly'
    });
    const [overBudget, setOverBudget] = useState(false);
    const time = convertMinutesToHours(project.time?.total);
    const totalTime = addUpTime(time);
    const canSetBillingType = hasPermission('setProjectBilling');
    const canEditProjectName = hasPermission('editProjectBudget');
    const canEditCompleted = hasPermission('editCompletedProjects');

    const validBillingTypes = ['Parent', 'Hourly', 'Retainer', 'Fixed Price', 'Internal', 'Non Billable'];

    useEffect(() => {
        if (project) {
            setLocalBudget({
                quoted: project.budget?.quoted || 0,
                budgeted: project.budget?.budgeted || 0,
                billingType: project.billingTerms?.type || 'Hourly'
            });
        }
    }, [project]);

    useEffect(() => {
        setOverBudget(totalTime > localBudget.quoted);
    }, [totalTime, localBudget.quoted]);

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setIsEditing(false);
        setLocalBudget({
            quoted: project?.budget?.quoted || 0,
            budgeted: project?.budget?.budgeted || 0,
            billingType: project?.billingTerms?.type || 'Hourly'
        });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setLocalBudget(prev => {
            if (name === 'quoted') {
                const parsedValue = parseFloat(value);
                return { ...prev, [name]: isNaN(parsedValue) ? 0 : parsedValue };
            }
            if (name === 'budgeted') {
                const parsedValue = parseFloat(value);
                return { ...prev, [name]: isNaN(parsedValue) ? 0 : parsedValue };
            }
            return { ...prev, [name]: value };
        });
    };

    const handleSave = () => {
        if (isLocked) return;
        const projectData = {
            budget: { quoted: localBudget.quoted, budgeted: localBudget.budgeted },
            billingTerms: { type: localBudget.billingType }
        };
        dispatch(updateProject({ projectId: project._id, projectData }));
        setIsEditing(false);
    };

    const showBudget = isEditing || (localBudget.quoted > 0);

    return (
        <InfoCard>
            <CardHeader
                title={
                    <CardHeaderContent>
                        <CardTitleText>Budget & Time</CardTitleText>
                        {(project?.completedDate === null || canEditCompleted) && canEditProjectName && !isLocked && (
                            <ButtonGroup>
                                {!isEditing ? (
                                    <IconButton onClick={handleEdit}>
                                        <Edit />
                                    </IconButton>
                                ) : (
                                    <>
                                        <IconButton onClick={handleSave}>
                                            <Check />
                                        </IconButton>
                                        <IconButton onClick={handleCancel}>
                                            <Close />
                                        </IconButton>
                                    </>
                                )}
                            </ButtonGroup>
                        )}
                    </CardHeaderContent>
                }
            />
            <CardContent>
                {showBudget && (
                    <>
                        <ContentGridSpaced>
                            <Typography>Project Billing Type</Typography>
                            <Typography>{localBudget.billingType}</Typography>
                        </ContentGridSpaced>
                        {canSetBillingType && isEditing && (
                            <ContentGridSpaced>
                                <Typography>Billing Type</Typography>
                                <Select
                                    name="billingType"
                                    value={localBudget.billingType}
                                    onChange={handleChange}
                                    size="small"
                                >
                                    {validBillingTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </ContentGridSpaced>
                        )}
                        {canSetBillingType && (
                            <ContentGridSpaced>
                                <Typography>Budgeted</Typography>
                                {!isEditing ? (
                                    <Typography>{localBudget.budgeted} Hrs</Typography>
                                ) : (
                                    <TextField
                                        type="number"
                                        name="budgeted"
                                        value={localBudget.budgeted}
                                        onChange={handleChange}
                                        size="small"
                                        inputProps={{ min: 0, step: 0.5 }}
                                    />
                                )}
                            </ContentGridSpaced>
                        )}
                        <ContentGridSpaced>
                            <Typography>{canSetBillingType ? 'Quoted' : 'Approved Budget'}</Typography>
                            {!isEditing ? (
                                <Typography>{localBudget.quoted} Hrs</Typography>
                            ) : (
                                <TextField
                                    type="number"
                                    name="quoted"
                                    value={localBudget.quoted}
                                    onChange={handleChange}
                                    size="small"
                                    inputProps={{ min: 0, step: 0.5 }}
                                />
                            )}
                        </ContentGridSpaced>
                        <LinearProgress
                            color={overBudget ? "error" : "primary"}
                            value={timePercentage(totalTime, localBudget.quoted)}
                            variant="determinate"
                        />
                        <ProgressBarCaption variant="body1">
                            {totalTime} / {localBudget.quoted} Hrs
                        </ProgressBarCaption>
                    </>
                )}

                {Object.keys(time).length > 0 ? (
                    Object.keys(time).map((keyName, i) => (
                        <React.Fragment key={i}>
                            <ContentGridSpaced>
                                <Typography>Time Spent ({keyName})</Typography>
                                <Typography>{time[keyName]}</Typography>
                            </ContentGridSpaced>
                            <LinearProgress
                                value={showBudget ? timePercentage(time[keyName], localBudget.quoted) : 100}
                                variant="determinate"
                                color={showBudget && time[keyName] > localBudget.quoted ? "error" : "primary"}
                            />
                            <ProgressBarCaption variant="body1">
                                {time[keyName]}{showBudget ? ` / ${localBudget.quoted} Hrs Used` : ''}
                            </ProgressBarCaption>
                        </React.Fragment>
                    ))
                ) : (
                    <>
                        <ContentGridSpaced>
                            <Typography>Time Spent</Typography>
                            <Typography>0 Hrs</Typography>
                        </ContentGridSpaced>
                        <LinearProgress
                            value={showBudget ? timePercentage(0, 0) : 100}
                            variant="determinate"
                            color="primary"
                        />
                        <ProgressBarCaption variant="body1">
                            0 Hrs Used
                        </ProgressBarCaption>
                    </>
                )}
            </CardContent>
        </InfoCard>
    );
};

export default ProjectBudgetTime;